import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { FiUser, FiLock } from 'react-icons/fi';

/**
 * Settings - דף הגדרות ופרופיל
 * מאפשר למשתמש לעדכן פרטים אישיים, אחוז מעשר ולשנות סיסמה
 */
const Settings = () => {
  const auth = useAuth();

  /** סטייט לטופס פרטים אישיים */
  const [profileData, setProfileData] = useState({
    first_name: auth.user?.first_name || '',
    last_name: auth.user?.last_name || '',
    email: auth.user?.email || '',
    maaser_percentage: auth.user?.maaser_percentage ?? 10,
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  /** סטייט לטופס שינוי סיסמה */
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  /**
   * עדכון שדה בטופס פרטים אישיים
   */
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * עדכון שדה בטופס שינוי סיסמה
   */
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * שליחת טופס עדכון פרטים אישיים
   * שולח את הנתונים לשרת ומעדכן את הקונטקסט
   */
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setProfileLoading(true);

    try {
      const response = await authAPI.updateMe({
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        email: profileData.email,
        maaser_percentage: Number(profileData.maaser_percentage),
      });
      // עדכון נתוני המשתמש בקונטקסט
      auth.updateUser(response.data.user || response.data);
      setProfileSuccess('הפרטים עודכנו בהצלחה.');
    } catch (err) {
      setProfileError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'שגיאה בעדכון הפרטים. אנא נסה שנית.'
      );
    } finally {
      setProfileLoading(false);
    }
  };

  /**
   * שליחת טופס שינוי סיסמה
   * מאמת התאמת סיסמאות לפני שליחה לשרת
   */
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // אימות התאמת סיסמאות חדשות
    if (passwordData.new_password !== passwordData.confirm_password) {
      setPasswordError('הסיסמאות החדשות אינן תואמות.');
      return;
    }

    setPasswordLoading(true);

    try {
      await authAPI.changePassword({
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      });
      setPasswordSuccess('הסיסמה שונתה בהצלחה.');
      // איפוס הטופס לאחר הצלחה
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (err) {
      setPasswordError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'שגיאה בשינוי הסיסמה. אנא בדוק את הסיסמה הנוכחית ונסה שנית.'
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="page">
      {/* כותרת הדף */}
      <div className="page-header">
        <h1>הגדרות</h1>
        <p className="page-subtitle">ניהול פרופיל והגדרות חשבון</p>
      </div>

      {/* כרטיס פרטים אישיים */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiUser />
            פרטים אישיים
          </h3>
        </div>

        {profileSuccess && <div className="alert alert-success">{profileSuccess}</div>}
        {profileError && <div className="alert alert-error">{profileError}</div>}

        <form onSubmit={handleProfileSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="first_name">שם פרטי</label>
              <input
                id="first_name"
                type="text"
                name="first_name"
                className="form-control"
                value={profileData.first_name}
                onChange={handleProfileChange}
                placeholder="הזן שם פרטי"
                required
                disabled={profileLoading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="last_name">שם משפחה</label>
              <input
                id="last_name"
                type="text"
                name="last_name"
                className="form-control"
                value={profileData.last_name}
                onChange={handleProfileChange}
                placeholder="הזן שם משפחה"
                required
                disabled={profileLoading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">אימייל</label>
            <input
              id="email"
              type="email"
              name="email"
              className="form-control"
              value={profileData.email}
              onChange={handleProfileChange}
              placeholder="הזן כתובת אימייל"
              required
              disabled={profileLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="maaser_percentage">אחוז מעשר (%)</label>
            <input
              id="maaser_percentage"
              type="number"
              name="maaser_percentage"
              className="form-control"
              value={profileData.maaser_percentage}
              onChange={handleProfileChange}
              min="0"
              max="100"
              step="0.1"
              required
              disabled={profileLoading}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={profileLoading}
          >
            {profileLoading ? 'שומר...' : 'שמור שינויים'}
          </button>
        </form>
      </div>

      {/* כרטיס שינוי סיסמה */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FiLock />
            שינוי סיסמה
          </h3>
        </div>

        {passwordSuccess && <div className="alert alert-success">{passwordSuccess}</div>}
        {passwordError && <div className="alert alert-error">{passwordError}</div>}

        <form onSubmit={handlePasswordSubmit}>
          <div className="form-group">
            <label htmlFor="old_password">סיסמה נוכחית</label>
            <input
              id="old_password"
              type="password"
              name="old_password"
              className="form-control"
              value={passwordData.old_password}
              onChange={handlePasswordChange}
              placeholder="הזן סיסמה נוכחית"
              required
              disabled={passwordLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="new_password">סיסמה חדשה</label>
            <input
              id="new_password"
              type="password"
              name="new_password"
              className="form-control"
              value={passwordData.new_password}
              onChange={handlePasswordChange}
              placeholder="הזן סיסמה חדשה"
              required
              disabled={passwordLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirm_password">אימות סיסמה חדשה</label>
            <input
              id="confirm_password"
              type="password"
              name="confirm_password"
              className="form-control"
              value={passwordData.confirm_password}
              onChange={handlePasswordChange}
              placeholder="הזן סיסמה חדשה שנית"
              required
              disabled={passwordLoading}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={passwordLoading}
          >
            {passwordLoading ? 'משנה סיסמה...' : 'שנה סיסמה'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Settings;
